import { useCallback, useEffect, useState } from "react";
import { Minus, Pencil, Plus, Target, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fieldErrs } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { EmptyState, Field, Loader, PageHeader } from "@/components/common";
import { Celebration, Confirm, EmojiPicker, FormDialog, ProgressBar } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";

const RGB = "var(--glow-money)";

function GoalForm({ goal, hubId, onClose, onSaved }) {
  const [f, setF] = useState({ name: goal?.name || "", emoji: goal?.emoji || "🎯", target: goal ? String(goal.target) : "", deadline: goal?.deadline || "" });
  const [errors, setErrors] = useState({});
  const save = async () => {
    const errs = {};
    if (!f.name.trim()) errs.name = "Add a name";
    const t = Number(f.target);
    if (!(t >= 1 && t <= 1000000)) errs.target = "Target must be between $1 and $1,000,000";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      const body = { ...f, target: t, deadline: f.deadline || null };
      if (goal) await api.put(`/organizations/${hubId}/goals/${goal.id}`, body); else await api.post(`/organizations/${hubId}/goals`, body);
      toast.success(goal ? "Goal updated" : "Goal added");
      onSaved();
    } catch (e) { setErrors(fieldErrs(e) || { form: errMsg(e) }); }
  };
  return (
    <FormDialog open onClose={onClose} title={goal ? "Edit goal" : "New savings goal"} testid="goal-form">
      <div className="space-y-4">
        <Field label="Name" htmlFor="goal-name" error={errors.name} errorTestid="goal-name-error">
          <input id="goal-name" className="q-input" maxLength={80} value={f.name} aria-invalid={!!errors.name} placeholder="Pool Pass" onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="goal-name-input" />
        </Field>
        <Field label="Emoji"><EmojiPicker value={f.emoji} onChange={(v) => setF({ ...f, emoji: v })} testid="goal-emoji" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Target ($)" htmlFor="goal-target" error={errors.target} errorTestid="goal-target-error">
            <input id="goal-target" type="number" step="1" min="0" className="q-input" value={f.target} aria-invalid={!!errors.target} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="goal-target-input" />
          </Field>
          <Field label="Deadline (optional)" htmlFor="goal-deadline">
            <input id="goal-deadline" type="date" className="q-input" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="goal-deadline-input" />
          </Field>
        </div>
        {errors.form && <p className="q-error">{errors.form}</p>}
        <div className="flex justify-end gap-2">
          <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="goal-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary" onClick={save} data-testid="goal-save-btn">Save goal</button>
        </div>
      </div>
    </FormDialog>
  );
}

function EntryDialog({ mode, goal, hubId, today, onClose, onSaved }) {
  const [f, setF] = useState({ amount: "", date: today, note: "" });
  const [error, setError] = useState("");
  const save = async () => {
    setError("");
    if (!(Number(f.amount) > 0)) { setError("Amount must be more than $0"); return; }
    try {
      const { data } = await api.post(`/organizations/${hubId}/goals/${goal.id}/${mode}`, { ...f, amount: Number(f.amount) });
      onSaved(data, goal);
    } catch (e) { setError(errMsg(e)); }
  };
  return (
    <FormDialog open onClose={onClose} title={mode === "contribute" ? `Add to ${goal.name}` : `Take out of ${goal.name}`} testid={`goal-${mode}-dialog`}>
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount ($)" htmlFor="entry-amount"><input id="entry-amount" type="number" step="0.01" min="0.01" className="q-input" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="goal-entry-amount" /></Field>
          <Field label="Date" htmlFor="entry-date"><input id="entry-date" type="date" className="q-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="goal-entry-date" /></Field>
        </div>
        <Field label="Note (optional)" htmlFor="entry-note"><input id="entry-note" className="q-input" maxLength={120} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="goal-entry-note" /></Field>
        {error && <p className="q-error" data-testid="goal-entry-error">{error}</p>}
        <div className="flex justify-end gap-2">
          <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="goal-entry-cancel">Cancel</button>
          <button className="q-btn q-btn-primary" onClick={save} data-testid="goal-entry-save">{mode === "contribute" ? "Add money" : "Take money out"}</button>
        </div>
      </div>
    </FormDialog>
  );
}

function GoalCard({ g, canManage, onEdit, onDelete, onEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="q-card space-y-4" data-testid={`goal-${g.id}`} style={g.reached ? { borderColor: `rgba(${RGB},0.55)`, boxShadow: `0 0 30px rgba(${RGB},0.15)` } : undefined}>
      <div className="flex items-start gap-3">
        <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-2xl" style={{ background: `rgba(${RGB},0.12)` }}>{g.emoji}</span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg font-extrabold text-q-text" data-testid={`goal-name-${g.id}`}>{g.name}</p>
          <p className="text-sm text-q-muted" data-testid={`goal-amounts-${g.id}`}><span className="font-bold text-q-text">{money(g.saved)}</span> of {money(g.target)}</p>
        </div>
        {g.reached && <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black uppercase tracking-[0.12em]" style={{ background: `rgba(${RGB},0.16)`, color: `rgb(${RGB})` }} data-testid={`goal-reached-${g.id}`}><Trophy className="h-3.5 w-3.5" /> Reached!</span>}
      </div>
      <ProgressBar percent={g.percent} rgb={RGB} testid={`goal-progress-${g.id}`} />
      <div className="flex flex-wrap justify-between gap-2 text-sm">
        <span className="font-bold text-q-text" data-testid={`goal-percent-${g.id}`}>{g.percent}%</span>
        <span className="text-q-muted" data-testid={`goal-left-${g.id}`}>{money(g.left)} left</span>
      </div>
      {g.deadline && <p className="text-xs text-q-muted">Deadline {fmtDate(g.deadline)}</p>}
      {g.pace && <p className="text-sm font-semibold text-q-text" data-testid={`goal-pace-${g.id}`}>{g.pace}</p>}
      <div className="flex flex-wrap gap-2">
        {canManage && (
          <>
            <button className="q-btn q-btn-primary py-2 text-xs" onClick={() => onEntry("contribute", g)} data-testid={`goal-contribute-btn-${g.id}`}><Plus className="h-3.5 w-3.5" /> Add money</button>
            <button className="q-btn q-btn-secondary py-2 text-xs" onClick={() => onEntry("withdraw", g)} data-testid={`goal-withdraw-btn-${g.id}`}><Minus className="h-3.5 w-3.5" /> Take out</button>
            <button className="q-btn q-btn-ghost px-2 py-2" onClick={() => onEdit(g)} aria-label="Edit goal" data-testid={`goal-edit-btn-${g.id}`}><Pencil className="h-4 w-4" /></button>
            <button className="q-btn q-btn-ghost px-2 py-2 hover:text-red-300" onClick={() => onDelete(g)} aria-label="Delete goal" data-testid={`goal-delete-btn-${g.id}`}><Trash2 className="h-4 w-4" /></button>
          </>
        )}
        <button className="q-btn q-btn-ghost py-2 text-xs" onClick={() => setOpen(!open)} data-testid={`goal-history-btn-${g.id}`}>{open ? "Hide history" : `History (${g.history.length})`}</button>
      </div>
      {open && (
        <ul className="divide-y divide-q-border rounded-lg border border-q-border" data-testid={`goal-history-${g.id}`}>
          {g.history.map((e) => (
            <li key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="w-20 text-xs text-q-faint">{fmtDate(e.date, { month: "short", day: "numeric", year: "2-digit" })}</span>
              <span className="min-w-0 flex-1 truncate text-q-muted">{e.note || (e.amount >= 0 ? "Contribution" : "Withdrawal")}</span>
              <span className="font-bold tabular-nums" style={{ color: e.amount >= 0 ? `rgb(${RGB})` : "rgb(var(--q-danger-text))" }}>{e.amount >= 0 ? "+" : "−"}{money(Math.abs(e.amount))}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export default function SavingsGoals() {
  const { hub } = useHub();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [entry, setEntry] = useState(null);
  const [del, setDel] = useState(null);
  const [party, setParty] = useState(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/goals`).then(({ data: d }) => setData(d)), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(), (m) => ["goal"].includes(m.entity), null);
  if (!data) return <Loader />;
  const remove = async () => {
    try { await api.delete(`/organizations/${hub.id}/goals/${del.id}`); toast.success("Goal deleted"); setDel(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const entrySaved = (g, before) => {
    setEntry(null);
    if (g.reached && !before.reached) setParty(true);
    toast.success("Goal updated");
    load();
  };

  return (
    <div data-testid="goals-page">
      <PageHeader eyebrow="Money" title="Savings Goals" rgb={RGB} testid="goals-title"
        actions={data.can_manage && <button className="q-btn q-btn-primary" onClick={() => setForm({})} data-testid="new-goal-btn"><Plus className="h-4 w-4" /> New goal</button>} />
      {!data.can_manage && <p className="mb-6 text-sm text-q-muted q-onwall" data-testid="goals-read-only">You can see the family's goals. Only parents can change them.</p>}
      {data.goals.length === 0 ? <EmptyState Icon={Target} title="No savings goals yet" hint="Save together for a trip, a car or a rainy day." rgb={RGB} testid="goals-empty" /> : (
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.goals.map((g) => <GoalCard key={g.id} g={g} canManage={data.can_manage} onEdit={(x) => setForm({ goal: x })} onDelete={setDel} onEntry={(mode, x) => setEntry({ mode, goal: x })} />)}
        </ul>
      )}
      {form && <GoalForm goal={form.goal} hubId={hub.id} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {entry && <EntryDialog mode={entry.mode} goal={entry.goal} hubId={hub.id} today={data.today} onClose={() => setEntry(null)} onSaved={entrySaved} />}
      <Confirm open={!!del} onClose={() => setDel(null)} title="Delete this goal?" body={del ? `${del.emoji} ${del.name} and its history will be removed.` : ""} onConfirm={remove} testid="goal-delete-confirm" />
      {party && <Celebration emoji="🏆" text="Goal reached!" onDone={() => setParty(false)} />}
    </div>
  );
}
